import React, { useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { UserContext } from '../../../Contexts/UserContext';
import Constants from '../../../Constants';
import { authGenerateUrl, authVerifyUrl, screenerListExport } from '../../../Config/config';

const SCREENER_COLUMNS = [
  "Company Name",
  "CIN",
  "Registered Name",
  "Latest Deal Date",
  "Latest Deal Round",
  "Latest Deal Amount(USD $M)",
  "Latest Deal Amount(INR Cr)",
  "Latest Fy",
  "Latest Revenue(INR Cr)",
  "Latest EBITDA(INR Cr)",
  "Latest PAT(INR Cr)",
  "Company Valuation (INR Cr)",
  "EBITDA Margin(%)",
  "PAT Margin(%)",
  "Operating Margin(%)",
  "Contribution Margin(%)",
  "Revenue Growth 3Y(%)",
  "Ebitda Growth 3Y(%)",
  "Pat Growth 3Y(%)"
];


export const useScreenerExport = ({ fileName = "screener_export", filters = {}, viewMode = 'filtered', selectedScreenerId = null } = {}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authCode, setAuthCode] = useState("");
  const [reachedLimitPopup, setReachedLimitPopup] = useState({ show: false, message: "" });
  const [alert, setAlert] = useState({ show: false, message: Constants.excelPopupMessage });
  const { getToken, logout, authAlert } = useContext(UserContext);

  const downloadExcel = useCallback(async () => {
    setIsExporting(true);
    try {
      const token = getToken();
      
      const payload = selectedScreenerId
        ? {
            screener_id: selectedScreenerId,
            list_type: viewMode,
            screener_columns: SCREENER_COLUMNS
          }
        : {
            ...filters,
            list_type: viewMode,
            screener_columns: SCREENER_COLUMNS
          };

      const response = await fetch(screenerListExport, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        if (response.status === Constants.sessionTerminatedStatus) {
          logout();
          return false;
        }
        if (response.status === 400) {
          const errorData = await response.json();
          return errorData?.message || "An error occurred while exporting.";
        }
        throw new Error('Export failed');
      }

      const contentType = response.headers.get('content-type');

      if (contentType === 'application/json') {
        const data = await response.json();
        toast.success(data?.message || "The data has been sent to your registered email.", { position: 'top-center' });
      } else {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      return true;
    } catch (error) {
      console.error('Export error:', error);
      return false;
    } finally {
      setIsExporting(false);
    }
  }, [getToken, fileName, filters, viewMode, logout]);

  const handleExport = useCallback(async () => {
    if (authAlert()) {
      setLoading(true);
      try {
        const generateResponse = await axios.post(authGenerateUrl, {}, {
          headers: {
            "Authorization": getToken(),
          },
        });

        if (generateResponse.status === 200) {
          setReachedLimitPopup({
            show: true,
            message: "An authorization code has been sent to your registered email ID. Please enter it below to proceed with data export through the database.",
            isAuthRequired: true
          });
        }
      } catch (error) {
        console.error("Error generating authorization code:", error);
        toast.error("Failed to generate authorization code. Please try again.", { position: "top-center" });
      } finally {
        setLoading(false);
      }
      return;
    }

    const result = await downloadExcel();
    if (result !== true && result) {
      setReachedLimitPopup({ show: true, message: result, isAuthRequired: false });
    }
  }, [authAlert, getToken, downloadExcel]);

  const handleAuthCodeSubmit = useCallback(async (code) => {
    try {
      const verifyResponse = await axios.post(authVerifyUrl, { authCode: code }, {
        headers: {
          "Authorization": getToken(),
        },
      });

      if (verifyResponse.status === 200) {
        toast.success(verifyResponse?.data?.message, { position: "top-center" });
        localStorage.setItem(Constants.localStorageKeys.exportExpireAt, verifyResponse?.data?.export_expires_at);
        setReachedLimitPopup({ show: false, message: "" });

        const result = await downloadExcel();
        if (result !== true && result) {
          setReachedLimitPopup({ show: true, message: result, isAuthRequired: false });
        }
      }
    } catch (error) {
      console.error("Error verifying authorization code:", error.response?.data?.message);
      toast.error(error?.response?.data?.message, { position: "top-center" });
    }
  }, [getToken, downloadExcel]);

  const closePopup = useCallback(() => {
    setReachedLimitPopup({ show: false, message: "" });
  }, []);

  const showExportAlert = useCallback(() => {
    setAlert(prev => ({ ...prev, show: true }));
  }, []);

  const onAlertChange = useCallback(async (flag) => {
    if (flag === 1) {
      await handleExport();
    }
    setAlert(prev => ({ ...prev, show: false }));
  }, [handleExport]);

  return {
    isExporting,
    loading,
    authCode,
    setAuthCode,
    reachedLimitPopup,
    alert,
    showExportAlert,
    onAlertChange,
    handleExport,
    handleAuthCodeSubmit,
    closePopup,
    downloadExcel,
    SCREENER_COLUMNS
  };
};

// Auth Required Popup Component
export const AuthPopup = ({ show, message, authCode, setAuthCode, onSubmit, onClose }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-75 z-10">
      <div className="relative w-full max-w-lg mx-4 md:mx-0 bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="pt-5 px-5 flex justify-between items-center">
          <h1 className="text-lg font-bold text-gray-900">Authorization Required</h1>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <p className="text-sm text-gray-500">{message}</p>
          <div className="mt-4 pl-4 flex items-center gap-2">
            <input
              type="text"
              maxLength={8}
              className="w-28 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC8B20]"
              onChange={(e) => setAuthCode(e.target.value)}
              value={authCode}
            />
            <button
              onClick={() => onSubmit(authCode)}
              className="px-4 py-2 text-sm font-medium text-white bg-[#BC8B20] rounded-md shadow-sm hover:bg-[#a37517] focus:outline-none"
              disabled={authCode.length !== 8}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reached Limit Popup Component
export const ReachedLimitPopup = ({ show, message, onClose }) => {
  if (!show) return null;

  return (
    <div className='flex font-sans_book_body cursor-default justify-center items-center z-10 fixed left-0 top-0 w-screen h-screen bg-gray-500 bg-opacity-75 transition-opacity'>
      <div className='relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 mx-4 md:mx-0 w-[50%]'>
        <div className='w-full flex flex-col'>
          <div className="mt-3 text-center sm:mt-0 px-5 py-3 sm:ml-4 sm:text-left">
            <h1 className='text-left py-1 text-lg font-bold leading-6 text-gray-900'>Alert!</h1>
            <div className="mt-4">
              <p className="text-sm text-gray-500 text-start">{message}</p>
            </div>
          </div>
        </div>
        <div className='flex bg-gray-50 px-4 pb-3 flex-row-reverse'>
          <button 
            onClick={onClose} 
            className='inline-flex w-full justify-center font-sans_bold_body rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Combined Export Popup Component for use in Table
export const ExportPopupRenderer = ({ 
  reachedLimitPopup, 
  authCode, 
  setAuthCode, 
  handleAuthCodeSubmit, 
  closePopup,
  authAlert
}) => {
  if (!reachedLimitPopup.show) return null;

  if (reachedLimitPopup.isAuthRequired && authAlert()) {
    return (
      <AuthPopup
        show={reachedLimitPopup.show}
        message={reachedLimitPopup.message}
        authCode={authCode}
        setAuthCode={setAuthCode}
        onSubmit={handleAuthCodeSubmit}
        onClose={closePopup}
      />
    );
  }

  return (
    <ReachedLimitPopup
      show={reachedLimitPopup.show}
      message={reachedLimitPopup.message}
      onClose={closePopup}
    />
  );
};

export { SCREENER_COLUMNS };
// export default Export;